import React, { CSSProperties, useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, IconButton, TextField, Typography } from "@mui/material";
import {
  FormattingRule,
  FormattingRuleType,
  SeriesColors,
  createFormattingRule,
  deleteFormattingInstructions,
  getFormattingRules,
  updateFormattingInstruction,
  isPositionValid,
} from "@coolset/cs-ui";
import { SeriesColorPalettes } from "modules/MainSurveyEditor/helpers/colorPalette";
import { ColorInput } from "shared/components/Inputs/ColorInput";
import { MinusIcon } from "shared/icons/MinusIcon";
import { PlusIcon } from "shared/icons/PlusIcon";
import { SurveyLayouts } from "modules/MainSurveyEditor/helpers/blockForm";
import { ColorPaletteInput } from "shared/components/Inputs/ColorPaletteInput";

export const SeriesInputs = (props: SeriesInputsProps) => {
  const { formattingInstructions, onChange, onFocusedPosition, onUnfocusedPosition, chartType } = props;

  const seriesRules = useMemo(
    () => getFormattingRules(formattingInstructions, { ruleType: FormattingRuleType.Series }),
    [formattingInstructions]
  );

  const [seriesValues, setSeriesValues] = useState<Partial<FormattingRule>[]>(seriesRules);

  const handleAddSeries = useCallback(() => {
    setSeriesValues((prevSeriesValues) => [
      ...prevSeriesValues,
      {
        ruleType: FormattingRuleType.Series,
        position: "",
        color: SeriesColors[0],
      },
    ]);
  }, []);

  const handleChangeSeries = useCallback(
    (change: Partial<FormattingRule>, index: number) => {
      const newSeriesValues = [...seriesValues];
      const newRule = { ...newSeriesValues[index], ...change } as FormattingRule;
      newSeriesValues[index] = newRule;
      setSeriesValues(newSeriesValues);

      let newRules;
      let error;

      // TODO - add debounce
      
      if (newRule.ruleId) {
        [newRules, error] = updateFormattingInstruction(formattingInstructions, newRule);
      }
      else {
        [newRules, error] = createFormattingRule(formattingInstructions, newRule);
      }

      if (!error) {
        onChange(newRules);
      }
    },
    [formattingInstructions, onChange, seriesValues]
  );

  const handleDeleteSeries = useCallback(
    (index: number) => {
      const rule = seriesValues[index] as FormattingRule;
      if (rule.ruleId) {
        onChange(deleteFormattingInstructions(formattingInstructions, rule.ruleId)[0]);
        return;
      }

      const newSeriesValues = [...seriesValues];
      newSeriesValues.splice(index, 1);
      setSeriesValues(newSeriesValues);
    },
    [formattingInstructions, onChange, seriesValues]
  );

  useEffect(() => {
    setSeriesValues(seriesRules);
  }, [seriesRules]);

  return (
    <div>
      <Typography variant="h3" mb={2}>
        Series
      </Typography>
      <div className="_d-grid-lg-gap">
        {seriesValues.map((rule, index) => (
          <div key={rule.ruleId || index} className="_d-grid">
            <div>
              <div className="_d-flex-ali-center-jc-sb">
                <Typography variant="h4" color="text.secondary" mb={1}>
                  Legend
                </Typography>
                {index > 0 && (
                  <IconButton onClick={() => handleDeleteSeries(index)}>
                    <MinusIcon />
                  </IconButton>
                )}
              </div>

              <div className="col-container-sm-gap" style={{ "--col-template": "1fr auto" } as CSSProperties}>
                <TextField value={rule.name} onChange={(e) => handleChangeSeries({ name: e.target.value }, index)} />
                {/* TODO: Right now we have a different palette for pie charts. We have discussed with product that we'll need a global approach and only one component that works for every color palette.
                We will also need to have one color palette, while we currently have one in cs-ui and one in colorPalette.ts */}
                {chartType === SurveyLayouts.Pie ? (
                  <ColorPaletteInput
                    value={rule.color ?? SeriesColorPalettes[0][0]}
                    options={SeriesColorPalettes}
                    onChange={(color) => handleChangeSeries({ color }, index)}
                  />
                ) : (
                  <ColorInput
                    value={rule.color ?? SeriesColors[0]}
                    options={SeriesColors}
                    onChange={(color) => handleChangeSeries({ color }, index)}
                  />
                )}
              </div>
            </div>

            <div>
              <Typography variant="h4" color="text.secondary" mb={1}>
                Dataset{" "}
                <Box component="span" color="text.tertiary">
                  (required)
                </Box>
              </Typography>
              <TextField
                value={rule.position}
                onChange={(e) => {
                  onFocusedPosition(e.target.value);
                  handleChangeSeries({ position: e.target.value }, index)
                }}
                error={!isPositionValid(rule.position) && !!rule.position}
                helperText={!isPositionValid(rule.position) && !!rule.position && "Invalid position"}
                onFocus={() => onFocusedPosition(rule.position ?? '')}
                onBlur={onUnfocusedPosition}
              />
            </div>
          </div>
        ))}
        <Button variant="text" onClick={handleAddSeries} sx={{ justifySelf: "start" }}>
          <PlusIcon />
          Add Series
        </Button>
      </div>
    </div>
  );
};

export interface SeriesInputsProps {
  formattingInstructions: FormattingRule[];
  onChange: (rules: FormattingRule[]) => void;
  onFocusedPosition: (position: string) => void;
  onUnfocusedPosition: () => void;
  chartType: SurveyLayouts;
}
